<script setup lang="ts">
import { computed } from 'vue'
import { BookOpen } from '@lucide/vue'
import { Progress } from 'catalyst-vue'
import type { Comic } from '@tanko/shared'
import { useLibraryStore } from '../../stores/library'
import { useCloudLibraryStore } from '../../stores/cloud-library'
import { useLibraryEntriesStore } from '../../stores/library-entries'
import type { LibraryEntry } from '../../lib/library-entries'
import { comicStatus } from '../../lib/comic-status'
import { useComicCover } from '../../lib/comic-cover'
import { pickReadingProgress, readingProgressRatio } from '../../lib/reading-progress-summary'
import ComicCardMenu from './comic-card-menu.vue'
import ComicSelectBox from './comic-select-box.vue'
import ComicStatusDot from './comic-status-dot.vue'

const props = defineProps<{ entry: LibraryEntry }>()
const emit = defineEmits<{ info: [entry: LibraryEntry]; edit: [entry: LibraryEntry]; read: [entry: LibraryEntry]; backup: [entry: LibraryEntry]; relink: [entry: LibraryEntry]; remove: [entry: LibraryEntry] }>()
const store = useLibraryStore()
const cloud = useCloudLibraryStore()
const entries = useLibraryEntriesStore()
const comic = computed(() => props.entry.comic)
const selecting = computed(() => entries.selectionMode)
const selected = computed(() => entries.selection.has(props.entry.id))
// 封面邏輯與書架卡片同一份，因此延遲載入時機與替代文字不會分岔。
const coverComic = computed(() => props.entry.local ?? props.entry.comic)
const coverScope = computed(() => props.entry.local ? 'local' as const : 'cloud' as const)
const { element, cover, failed, fallbackText } = useComicCover(() => coverComic.value, () => coverScope.value)
const available = computed(() => props.entry.readable)
// 備份只有持有本機檔案的裝置做得到，與書架卡片同一條規則。
const canBackup = computed(() => !!props.entry.local && !props.entry.backedUp)
// 與書架卡片同一條規則：本機檔案遺失才有「更新位置」。
const canRelink = computed(() => !!props.entry.local && store.missing.has(props.entry.local.id))
// 狀態燈與書架卡片共用同一份判斷，只是列裡放小一號。
const status = computed(() => comicStatus({
  hasLocalCopy: !!props.entry.local,
  syncState: props.entry.comic.syncState,
  missing: !!props.entry.local && store.missing.has(props.entry.local.id),
  indexFailed: !!props.entry.local && !!store.indexErrors[props.entry.local.id],
  indexing: !!props.entry.local && store.indexing.has(props.entry.local.id),
}))
// 本機那一份記在本機識別碼上，雲端那一份記在目錄項上；取較新者，iPad 上沒有本機副本也看得到。
const progressPercent = computed(() => {
  const local = props.entry.local ? store.progress.get(props.entry.local.id) : undefined
  const remote = props.entry.comic.cloudComicId ? cloud.progress.get(props.entry.comic.cloudComicId) : undefined
  const picked = pickReadingProgress(local, remote)
  return picked ? Math.round(readingProgressRatio(picked.page, props.entry.comic.pageCount) * 100) : null
})
function selectAction(key: 'info' | 'organize' | 'backup' | 'relink' | 'remove') {
  if (key === 'info') { emit('info', props.entry); return }
  if (key === 'backup') { emit('backup', props.entry); return }
  if (key === 'relink') { emit('relink', props.entry); return }
  if (key === 'remove') { emit('remove', props.entry); return }
  emit('edit', props.entry)
}
</script>

<template>
  <li ref="element" class="comic-row" data-row-action-reveal-scope>
    <ComicSelectBox v-if="selecting" :selected="selected" :title="comic.title" @toggle="extend => entries.toggleSelected(entry.id, extend)" />
    <div class="comic-row-cover">
      <img v-if="cover && !failed" :src="cover" :alt="`${comic.title}，第 ${comic.coverPage + 1} 頁封面`" />
      <BookOpen v-else :size="18" :stroke-width="1.2" aria-hidden="true" />
    </div>
    <div class="comic-row-main">
      <button v-if="selecting" type="button" class="comic-row-title" @click="event => entries.toggleSelected(entry.id, event.shiftKey)">{{ comic.title }}</button>
      <button v-else-if="available" type="button" class="comic-row-title" @click="emit('read', entry)">{{ comic.title }}</button>
      <span v-else class="comic-row-title is-unavailable" :title="comic.title">{{ comic.title }}</span>
      <p class="comic-row-meta">
        <span>{{ comic.author || '作者未詳' }}</span>
        <span v-if="!available" class="comic-row-state">{{ fallbackText }}</span>
      </p>
    </div>
    <!--
      頁數、進度、選單收成一個靠右的群組。原本只有進度條孤零零貼在最右，中間留下
      一大片空白，看起來像沒做完；把頁數從 meta 移進來之後右側才有實體。
    -->
    <div class="comic-row-aside">
      <ComicStatusDot v-if="status" :status="status" size="sm" />
      <span class="comic-row-pages">{{ comic.pageCount }} 頁</span>
      <Progress
        v-if="progressPercent !== null"
        class="comic-row-progress"
        size="sm"
        color="primary"
        :value="progressPercent"
        :aria-label="`${comic.title} 的閱讀進度 ${progressPercent}%`"
      />
      <span v-else class="comic-row-progress is-empty" aria-hidden="true" />
      <ComicCardMenu
        class="comic-row-menu"
        :title="comic.title"
        :data-comic-id="entry.id"
        :can-backup="canBackup"
        :can-relink="canRelink"
        @select="selectAction"
      />
    </div>
  </li>
</template>
